import {
  Controller,
  FieldError,
  RegisterOptions,
  useFormContext,
  UseFormRegister,
} from 'react-hook-form';
import classNames from 'classnames';
import { PatternFormat, PatternFormatProps } from 'react-number-format';
import { get } from 'lodash';
import { IconType } from '@react-icons/all-files/lib';
import { forwardRef } from 'react';
import { FormControl } from './FormControl';

import { Testable } from '../types';
import { useFormFieldState } from './hooks';
import { InputDefaultClassName } from '../../utils';

function getInputClassNames({
  leftIcon,
  rightIcon,
  error,
  disabled,
  leftText,
  className,
}: {
  leftIcon?: IconType;
  rightIcon?: IconType;
  leftText?: React.ReactNode;
  disabled?: boolean;
  error?: boolean;
  className?: string;
}) {
  return classNames(
    InputDefaultClassName,
    leftText
      ? 'sm:pl-1 sm:rounded-r-md sm:border-l-0 rounded-r-md'
      : 'rounded-md',
    leftIcon && 'pl-9',
    rightIcon && 'pr-9',
    disabled ? 'bg-gray-100 overflow-ellipsis' : 'shadow-sm',
    error ? 'border-red-600' : 'border-gray-300',
    className,
  );
}

export interface InputProps
  extends Testable<React.InputHTMLAttributes<HTMLInputElement>> {
  label?: React.ReactNode;
  options?: RegisterOptions;
  name: string;
  leftIcon?: IconType;
  register?: UseFormRegister<any>;
  rightIcon?: IconType;
  onRightIconClick?: () => void;
  leftText?: React.ReactNode;
  error?: any;
}

type BasicInputProps = Omit<InputProps, 'name'>;

export const BasicInput = forwardRef<HTMLInputElement, BasicInputProps>(
  // TODO fix
  // eslint-disable-next-line react/prop-types
  ({ leftIcon, rightIcon, leftText, type = 'text', ...restProps }, ref) => (
    <div className="flex items-center w-full">
      {!!leftText && (
        <p className="block text-sm text-gray-500 flex-1 py-3.5 md:py-3 border-t border-b border-l pl-3 pr-1 rounded-l bg-gray-200 whitespace-nowrap">
          {leftText}
        </p>
      )}
      <input
        ref={ref}
        type={type}
        {...restProps}
        onSubmit={restProps.onSubmit}
        className={getInputClassNames({
          leftText,
          leftIcon,
          rightIcon,
          error: !!restProps.error,
          disabled: restProps.disabled,
          className: restProps.className,
        })}
      />
    </div>
  ),
);

export function Input({
  label,
  options,
  leftIcon,
  rightIcon,
  onRightIconClick,
  leftText,
  type = 'text',
  ...restProps
}: InputProps) {
  const { register, isSubmitting, error } = useFormFieldState(restProps);
  const formInputProps = register(restProps.name, options);

  return (
    <FormControl
      leftIcon={leftIcon}
      rightIcon={rightIcon}
      onRightIconClick={onRightIconClick}
      error={error as FieldError}
      name={restProps.name}
      label={label}
    >
      <BasicInput
        leftText={leftText}
        leftIcon={leftIcon}
        rightIcon={rightIcon}
        {...restProps}
        {...formInputProps}
        error={error}
        type={type}
        disabled={isSubmitting || restProps.disabled}
        onBlur={async (e) => {
          restProps.onBlur?.(e);
          await formInputProps.onBlur(e);
        }}
        onChange={async (e) => {
          restProps.onChange?.(e);
          await formInputProps.onChange(e);
        }}
      />
    </FormControl>
  );
}

export function PhoneNumberInput(
  props: Testable<InputProps> &
    PatternFormatProps & { formattingInState?: boolean },
) {
  const { formattingInState, leftIcon, ...inputProps } = props;
  const {
    control,
    formState: { errors, isSubmitting },
  } = useFormContext();
  const error = get(errors, props.name!);

  return (
    <FormControl
      name={props.name!}
      leftIcon={props.leftIcon}
      label={props.label}
      error={error as FieldError}
    >
      <Controller
        name={props.name!}
        control={control}
        defaultValue={props.defaultValue || ''}
        render={({ field: { onBlur, onChange, name, ref, value } }) => (
          <PatternFormat
            {...inputProps}
            name={name}
            className={classNames(
              getInputClassNames({
                leftIcon,
                disabled: isSubmitting || props.disabled,
                error: !!error,
              }),
              props.className,
            )}
            onBlur={onBlur}
            getInputRef={ref}
            type="tel"
            value={value}
            onValueChange={(values) =>
              onChange(formattingInState ? values.formattedValue : values.value)
            }
          />
        )}
      />
    </FormControl>
  );
}
